import { useCallback, useEffect, useRef, useState } from 'react';
import {
  dashboardService,
  type ConstellationDetail,
} from '../../../services/dashboard.service';

/** The backend's per-call cap on /bucketed-constellation/details. */
const MAX_IDS_PER_CALL = 50;

/**
 * Long enough to coalesce a fast drag across a dense scatter into one request,
 * short enough that a tooltip does not visibly wait on it.
 */
const FLUSH_MS = 60;

export interface ConstellationDetails {
  /**
   * Cached detail for `donorId`, or undefined if it has not landed yet.
   * Reads a ref, so the value is always current — but a component that RENDERS
   * from it must also depend on `get`'s identity (or on `version`) to repaint
   * when a batch arrives.
   */
  get: (donorId: string) => ConstellationDetail | undefined;
  /** Fire-and-forget: queue these ids if they are not cached or already in flight. */
  ensure: (donorIds: string[]) => void;
  /** Resolves once `donorId` is cached, or once the lookup for it has failed. */
  load: (donorId: string) => Promise<void>;
  /** Increments whenever a batch lands. */
  version: number;
}

/**
 * The descriptive half of a constellation dot — name, employer, batch — fetched
 * by donor_id and cached forever.
 *
 * /bucketed-constellation deliberately ships only what a dot needs to be drawn,
 * because carrying names and employers for all ~25k rows was ~45% of a 7.4 MB
 * payload. This is the other half, fetched for the handful of rows actually
 * being looked at: hovered dots one at a time, and the alumni list's current
 * window fifty at a time.
 *
 * Caching is permanent and never keyed on filters: a person's name and employer
 * do not depend on which buckets are selected, so a row fetched under one
 * selection stays correct under the next.
 */
export const useConstellationDetails = (): ConstellationDetails => {
  const cache = useRef<Map<string, ConstellationDetail>>(new Map());
  /** Queued but not yet sent. */
  const queue = useRef<Set<string>>(new Set());
  /** Queued OR in flight — the guard that stops a re-render re-requesting a window. */
  const pending = useRef<Set<string>>(new Set());
  const waiters = useRef<Map<string, Array<() => void>>>(new Map());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);
  const [version, setVersion] = useState(0);

  const flush = useCallback(() => {
    timer.current = null;

    const all = Array.from(queue.current);
    queue.current.clear();
    if (!all.length) return;

    // A window wider than one call goes out as parallel requests rather than a
    // chain of them. MAX_IDS_PER_CALL is the endpoint's per-call cap, not a
    // rate limit, and the client only ever queues rows it is about to show —
    // in practice one batch, occasionally two.
    for (let i = 0; i < all.length; i += MAX_IDS_PER_CALL) {
      const ids = all.slice(i, i + MAX_IDS_PER_CALL);

      const settle = () => {
        ids.forEach((id) => {
          pending.current.delete(id);
          waiters.current.get(id)?.forEach((fn) => fn());
          waiters.current.delete(id);
        });
        if (mounted.current) setVersion((v) => v + 1);
      };

      dashboardService
        .getConstellationDetails(ids)
        .then((res) => {
          (res.data ?? []).forEach((d) => cache.current.set(d.donor_id, d));
          // An id the lookup answered nothing for is a profile that no longer
          // exists. Cache a stand-in so it settles on the donor_id instead of
          // being retried on every scroll. Only on success — a failed request
          // must stay retryable.
          ids.forEach((id) => {
            if (!cache.current.has(id)) {
              cache.current.set(id, {
                donor_id: id,
                name: id,
                cumulative_giving: 0,
                confidence: 0,
              });
            }
          });
        })
        .catch((err) => {
          // Never an error state: everything numeric renders from data already
          // on the client, so a failed lookup costs a name, not the view.
          console.error('Failed to load constellation detail', err);
        })
        .finally(settle);
    }
  }, []);

  const enqueue = useCallback(
    (ids: string[]) => {
      let added = false;
      ids.forEach((id) => {
        if (!id || cache.current.has(id) || pending.current.has(id)) return;
        pending.current.add(id);
        queue.current.add(id);
        added = true;
      });
      if (added && !timer.current) timer.current = setTimeout(flush, FLUSH_MS);
    },
    [flush],
  );

  const load = useCallback(
    (donorId: string): Promise<void> => {
      if (cache.current.has(donorId)) return Promise.resolve();
      return new Promise<void>((resolve) => {
        const existing = waiters.current.get(donorId);
        if (existing) existing.push(resolve);
        else waiters.current.set(donorId, [resolve]);
        // No-op when the id is already queued by a window fetch; the waiter
        // registered above still resolves when that batch settles.
        enqueue([donorId]);
      });
    },
    [enqueue],
  );

  // Identity changes with `version` so consumers that render from the cache
  // repaint when a batch lands, without having to name `version` themselves.
  const get = useCallback(
    (donorId: string) => cache.current.get(donorId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version],
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  return { get, ensure: enqueue, load, version };
};
